/**
 * useNotifications Hook
 * React hook for managing push notifications in Chess Kingdom
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import notificationService from '../services/NotificationService';

export const useNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const serviceRef = useRef(notificationService);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      await serviceRef.current.init();
      setPermission(serviceRef.current.permission);
      setSoundEnabled(serviceRef.current.soundEnabled);
      setInitialized(true);
    };
    init();
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const result = await serviceRef.current.requestPermission();
    setPermission(result);
    return result;
  }, []);

  // Toggle sound
  const toggleSound = useCallback((enabled) => {
    serviceRef.current.toggleSound(enabled);
    setSoundEnabled(enabled);
  }, []);

  // Notification methods — stable refs to module singleton
  const notifyYourTurn = useCallback((opponentName) => {
    return serviceRef.current.notifyYourTurn(opponentName);
  }, []);

  const notifyMatchFound = useCallback((roomName) => {
    return serviceRef.current.notifyMatchFound(roomName);
  }, []);

  const notifyGameInvite = useCallback((fromUser, roomName) => {
    return serviceRef.current.notifyGameInvite(fromUser, roomName);
  }, []);

  const notifyFriendRequest = useCallback((fromUser) => {
    return serviceRef.current.notifyFriendRequest(fromUser);
  }, []);

  const notifySpectatorRequest = useCallback((userName) => {
    return serviceRef.current.notifySpectatorRequest(userName);
  }, []);

  const notifyRejoinRequest = useCallback((userName, roomName) => {
    return serviceRef.current.notifyRejoinRequest(userName, roomName);
  }, []);

  const notifyMessage = useCallback((fromUser, preview) => {
    return serviceRef.current.notifyMessage(fromUser, preview);
  }, []);

  const notifyFriendOnline = useCallback((friendName) => {
    return serviceRef.current.notifyFriendOnline(friendName);
  }, []);

  const notifyGameOver = useCallback((result, reason) => {
    return serviceRef.current.notifyGameOver(result, reason);
  }, []);

  const testNotification = useCallback(() => {
    return serviceRef.current.testNotification();
  }, []);

  const playSound = useCallback((soundType) => {
    return serviceRef.current.playSound(soundType);
  }, []);

  return {
    // State
    permission,
    soundEnabled,
    initialized,
    isEnabled: permission === 'granted',
    
    // Actions
    requestPermission,
    toggleSound,
    playSound,
    testNotification,
    
    // Notification methods
    notifyYourTurn,
    notifyMatchFound,
    notifyGameInvite,
    notifyFriendRequest,
    notifySpectatorRequest,
    notifyRejoinRequest,
    notifyMessage,
    notifyFriendOnline,
    notifyGameOver
  };
};

export default useNotifications;
